import React from 'react';
import styled from 'styled-components/macro';
import { rgba } from 'polished';
import { useSelector } from 'react-redux';

import { Label, Data } from 'components/base/KeyValuePair/KeyValuePair';

import TrackMetadata from './entities/Track';
import ReleaseMetadata from './entities/Release';
import ArtistMetadata from './entities/Artist';
import LabelMetadata from './entities/Label';
import ProjectMetadata from './entities/Project';
import { apiTypeToEntityType } from 'helpers/mapEntityTypes';
import modularScale from 'styles/helpers/modular-scale';

import { checkIsExternalUser } from 'redux/reducers/authentication';
import { ContextTopics, EntityTypes } from 'context/entityContext';

export const MetadataWrapper = styled('div')`
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  justify-content: center;

  a {
    box-shadow: 0px 1px 0px 0px white;

    &:hover {
      box-shadow: 0px 2px 0px 0px white;
    }

    &:active,
    &:focus {
      color: white;
      background: ${({ theme }): string => rgba(theme.NightBlue, 0.1)};
    }

    svg {
      color: white;
      margin-left: ${({ theme }): string => theme.spacing.xxsmall};
    }
  }

  @media screen and (min-width: 40em) {
    flex-flow: column nowrap;
    align-items: flex-start;
  }
`;

export const Date = styled.span`
  white-space: nowrap;
`;

export const DefaultMetaData = styled('p')`
  color: white;
  margin: 0 0 1em;
  line-height: ${modularScale(3)};
  flex: 1 0 100%;

  span + span:before {
    content: ' • ';
    margin: 0 ${({ theme }): string => theme.spacing.xxsmall};
  }

  @media screen and (min-width: 40em) {
    margin-top: 0;
    flex: 1 0 auto;
  }
`;

export const ExpandableMetaData = styled('dl')`
  margin: 0;
  margin-bottom: 1em;
  text-align: left;
  flex: 0 1 auto;
  padding: 0;

  > div {
    display: flex;
    margin-bottom: ${({ theme }): string => theme.spacing.xsmall};
  }

  ${Label} {
    min-width: 7em;
    color: white;
    margin-top: ${modularScale(-6)};
  }

  ${Data} {
    font-weight: normal;
    font-size: ${modularScale(-1)};
    color: white;
    line-height: ${modularScale(2)};

    > span {
      border-bottom: 1px solid white;
    }
  }

  @media screen and (min-width: 40em) {
    flex: 1 0 auto;
    width: 100%;
  }
`;

export const ToggleDetailsButton = styled('button')`
  opacity: 0.8;
  color: white;
  font-size: ${modularScale(-1)};
  min-width: 11ch;
  transition: ${({ theme }): string =>
    `opacity ${theme.transitionDuration} ${theme.timingFunction}`};
  display: block;
  margin: ${({ theme }): string => theme.spacing.small} auto 0;
  padding: 0;
  line-height: 1;

  @media screen and (min-width: 40em) {
    display: inline;
    margin-top: 0;
    margin-left: ${({ theme }): string => theme.spacing.xsmall};
  }

  &:hover {
    opacity: 1;
  }
`;

const Placeholder = styled.div`
  height: 2.8em;
`;

const MetadataItems = (props: any): JSX.Element | null => {
  if (!props.entity) {
    return null;
  }

  const entityType = apiTypeToEntityType(props.entity.type);

  switch (entityType) {
    case 'track':
      return <TrackMetadata {...props} />;
    case 'release':
      return <ReleaseMetadata {...props} />;
    case 'artist':
      return <ArtistMetadata {...props} />;
    case 'label':
      return <LabelMetadata {...props} />;
    case 'project':
      return <ProjectMetadata {...props} />;
    default:
      return null;
  }
};

interface IEntityMetadataProps {
  /** This indicates whether the Entity is in loading state */
  loading: boolean;
  /** The current visible topic */
  topic: ContextTopics;
  /** The Entity data coming from the API */
  entityInfo?: any;
  entityType: EntityTypes;
  artists?: any;
}

/**
 * Wrapper container containing all EntityMetadataItems.
 * This component will determine which items to show.
 */
const EntityMetadata: React.FC<IEntityMetadataProps> = (props) => {
  const { entityInfo, loading, topic, entityType, artists } = props;

  const isExternalUser = useSelector(checkIsExternalUser);

  return (
    <MetadataWrapper>
      {!loading &&
      (artists || entityType === 'artist' || entityType === 'label') ? (
        <MetadataItems
          entity={entityInfo.entity}
          artists={artists}
          metadata={entityInfo.metadata}
          topic={topic}
          showLabel={!isExternalUser}
        />
      ) : entityType !== 'artist' ? (
        <Placeholder />
      ) : null}
      {loading && <DefaultMetaData>&nbsp;</DefaultMetaData>}
    </MetadataWrapper>
  );
};

export default EntityMetadata;
